import styled from '@emotion/styled';
import type { Prismic } from '@landsbankinn/prismic';
import { PrismicLink } from '@landsbankinn/prismic';
import { Divider, device, typography } from '@landsbankinn/ui';
import React from 'react';
import { SliceWrapper } from './SliceWrapper';

enum PointColor {
  AUTO = 'Auto',
  BLUE = 'Blue',
  LIGHTBLUE = 'LightBlue',
  CYAN = 'Cyan',
  ORANGE = 'Orange',
}
interface IProps {
  children: React.ReactNode;
  copy: React.ReactNode;
  label?: string;
  description?: React.ReactNode;
  color: 'Default' | 'Auto' | 'Blue' | 'LightBlue' | 'Cyan' | 'Orange';
}

interface IItem {
  number: React.ReactNode;
  text: React.ReactNode;
  link?: Prismic.Link;
  color: 'Default' | 'Auto' | 'Blue' | 'LightBlue' | 'Cyan' | 'Orange';
}

const autoColor = (props: any) => {
  const colors = [
    props.theme.shades.primary4,
    props.theme.shades.secondary4,
    props.theme.shades.ternary4,
    props.theme.shades.primary4,
    props.theme.shades.secondary4,
    props.theme.shades.ternary4,
  ];
  const styles = {};
  for (let index = 0; index < colors.length; index++) {
    (styles as any)[`&:nth-of-type(${index + 1})`] = {
      color: colors[index],
    };
  }
  return styles;
};

const Header = styled.div<{ color: string }>`
  margin-bottom: 20px;

  ${(props) => {
    switch (props.color) {
      case PointColor.AUTO:
        return autoColor;
      case PointColor.BLUE:
        return props.theme.colors.primary;
      case PointColor.LIGHTBLUE:
        return props.theme.shades.primary4;
      case PointColor.CYAN:
        return props.theme.shades.ternary4;
      case PointColor.ORANGE:
        return props.theme.shades.secondary4;
    }
    return props.theme.colors.primary;
  }};

  h3 {
    color: currentColor;
  }

  div {
    background-color: currentColor;
  }
`;

const Label = styled.h3`
  ${typography.bodyEditorial}
  font-weight: bold;
  margin-bottom: 28px;
  line-height: ${24 / 22};
  color: currentColor;
`;

const List = styled.div<{ noLabel: boolean }>`
  @media ${device.tablet} {
    margin-top: ${(props) => (props.noLabel ? '-12px' : 0)};
  }
`;

const Description = styled.div``;

const Item = styled.div<{ color: string }>`
  padding-top: 12px;
  padding-bottom: 44px;
  margin-bottom: 20px;
  border-bottom: 1px solid currentColor;

  ${(props) => {
    switch (props.color) {
      case PointColor.AUTO:
        return autoColor;
      case PointColor.BLUE:
        return props.theme.colors.primary;
      case PointColor.LIGHTBLUE:
        return props.theme.shades.primary4;
      case PointColor.CYAN:
        return props.theme.shades.ternary4;
      case PointColor.ORANGE:
        return props.theme.shades.secondary4;
    }
    return props.theme.colors.primary;
  }};
`;

const Number = styled.div`
  ${typography.numbersBig}
  margin-bottom: 10px;
`;
const Text = styled.div`
  ${typography.bodyEditorial}
  font-weight: bold;
  line-height: ${24 / 22};
`;

export const NumberItem = ({ number, text, color, link }: IItem) => {
  if (!!link) {
    return (
      <Item color={color}>
        <PrismicLink value={link}>
          <a>
            <Number>{number}</Number>
            <Text>{text}</Text>
          </a>
        </PrismicLink>
      </Item>
    );
  }

  return (
    <Item color={color}>
      <Number>{number}</Number>
      <Text>{text}</Text>
    </Item>
  );
};

export const TextWithNumbers = ({
  children,
  copy,
  label,
  description,
  color,
}: IProps) => {
  return (
    <SliceWrapper
      left={copy}
      right={
        <>
          {label && (
            <Header color={color}>
              <Label>{label}</Label>
              <Divider />
            </Header>
          )}
          <List noLabel={!label}>{children}</List>
          {description && <Description>{description}</Description>}
        </>
      }
      isArticle
    />
  );
};
